/**
 * KT1 packaging pipeline (FR-71, TECH-04): mono float samples at any device
 * rate → 16 kHz → frame-exact 30 s cap → 16-bit PCM WAV → strict validation.
 */

import { resample } from './resample.ts'
import {
  encodeWavPcm16,
  KT1_MAX_FRAMES,
  KT1_SAMPLE_RATE,
  validateKt1Wav,
  type WavInfo,
} from './wav.ts'

export interface Kt1Wav {
  wav: ArrayBuffer
  info: WavInfo
  inputRate: number
  inputFrames: number
  /** True when audio beyond 30.000 s was dropped. */
  truncated: boolean
  /** Time spent resampling and encoding, for latency budgeting. */
  processingMs: number
}

export function buildKt1Wav(samples: Float32Array, inputRate: number): Kt1Wav {
  const t0 = performance.now()
  const resampled = resample(samples, inputRate, KT1_SAMPLE_RATE)
  const truncated = resampled.length > KT1_MAX_FRAMES
  const capped = truncated ? resampled.subarray(0, KT1_MAX_FRAMES) : resampled
  const wav = encodeWavPcm16(capped, KT1_SAMPLE_RATE)
  const processingMs = performance.now() - t0

  const check = validateKt1Wav(wav)
  if (!check.ok || check.info === null) {
    // The pipeline must never emit a file the server would reject.
    throw new Error(`KT1 WAV failed validation: ${check.violations.join('; ')}`)
  }
  return {
    wav,
    info: check.info,
    inputRate,
    inputFrames: samples.length,
    truncated,
    processingMs,
  }
}
